import uniq from 'lodash/uniq'
import { Position } from '@turf/helpers'
import { atom, selector, selectorFamily } from 'recoil'

import { footfallClient, partnersClient, tradeAreaClient } from 'src/clients'
import {
  TradeAreaDateRange,
  DemoRadiiResponse,
  CommonTradeAreaSection,
  ConsumerSpendRadiusData,
  NearVenue,
  SimpleVenueWithDistance,
  TrendsResponse,
  TrendsPeriod,
  CommonTradeAreaDetailsResponse,
  RadiusRingsTradeAreaDetailsResponse,
  RadiusRingsTradeAreaDetailsRequest,
  PromiseCache,
  BuildoutTradeAreaDetailsRequest,
  BBox2d,
} from 'src/common-services'
import { DEFAULT_LRU_SIZE, keyFactory, retryAtom, uncachedSelector, uncachedSelectorFamily } from 'src/utils/recoil'
import { token } from 'src/atoms/buildout-frame'
import { googlePlace, venueMatch } from 'src/atoms/search'
import { ifNotFound } from 'src/utils/ajax'
import { selectedCategory } from 'src/components/insights/performance-benchmarks/atoms'
import { trendDatesByPeriod } from 'src/components/insights/common/visits/atoms'
import { computeHispanicPercentage } from 'src/atoms/demographics'
import { browserLogger } from 'src/utils/browser-logger'

import * as buildoutFrameAtoms from 'src/atoms/buildout-frame'
import * as atomsCompare from 'src/atoms/compare'
import * as atomsColors from 'src/atoms/colors'
import turf from 'src/utils/turf'

export const ALL_DEMOGRAPHICS_CATEGORIES = [
  'Overview',
  'Households',
  'Housing Units',
  'Gender',
  'Ethnicity',
  'Age',
  'Marital Status',
  'Value of Owner Occupied Housing Units',
  'Types of Housing Units',
  'Household Income',
  'Education',
  'Labor Force',
  'Transportation to Work',
  'Travel Time to Work',
  'Population by Industry',
  'Population by Occupation',
]

// Cached API Requests
const cacheGetTradeAreaDateRanges = new PromiseCache()
const cacheGetRadiiTradeAreaDetails = new PromiseCache()
const cacheGetRadiusRingsNearby = new PromiseCache()

const key = keyFactory('radius-rings-trade-area')

export enum TradeAreaDetailsTabs {
  OVERVIEW = 'OVERVIEW',
  CONSUMER = 'CONSUMER',
  DEMOGRAPHICS = 'DEMOGRAPHICS',
  BENCHMARKING = 'BENCHMARKING',
}

export const tab = atom<TradeAreaDetailsTabs>({
  key: key('tab'),
  default: TradeAreaDetailsTabs.OVERVIEW,
})

export const ttaCenterPoint = atom<Position | undefined>({
  key: key('tta-center-point'),
  default: undefined,
})

export const ttaPosition = selector<Position | undefined>({
  key: key('tta-position'),
  get: ({ get }) => {
    const centerPoint = get(ttaCenterPoint)
    if (centerPoint) {
      return centerPoint
    }
    const place = get(googlePlace)
    return place?.geometry.location
  },
})

export const radii = atom<number[]>({
  key: key('radii'),
  default: [1, 3, 5],
})

export const radiiCompare = atom<number[]>({
  key: key('radii-compare'),
  default: [1],
})

export const columnLabels = selector<string[]>({
  key: key('column-labels'),
  get: ({ get }) => {
    const rads = get(radii)
    return rads.map((rad) => `${rad} miles`)
  },
})

export const venue = uncachedSelector({
  key: key('venue'),
  get: async ({ get }) => {
    return get(venueMatch)
  },
  lru: DEFAULT_LRU_SIZE,
})

export const dates = selector<TradeAreaDateRange[]>({
  key: key('dates'),
  get: ({ get }) => {
    get(retryAtom)
    return ifNotFound(
      cacheGetTradeAreaDateRanges.get({}, () => tradeAreaClient.getTradeAreaDateRanges()),
      []
    )
  },
})

export const selectedDate = atom<number>({
  key: key('selected-date'),
  default: 0,
})

export const mapLoaded = selectorFamily<boolean, boolean>({
  key: key('map-loaded'),
  get:
    (compare: boolean) =>
    ({ get }) => {
      return true
    },
})

const request = uncachedSelectorFamily<
  RadiusRingsTradeAreaDetailsRequest | BuildoutTradeAreaDetailsRequest | undefined,
  Position | undefined
>({
  key: key('request'),
  get:
    (position) =>
    ({ get }) => {
      const buildoutToken = get(token)
      if (buildoutToken) {
        const configOverride = get(buildoutFrameAtoms.dataConfigOverride)
        const req: BuildoutTradeAreaDetailsRequest = {
          token: buildoutToken,
        }
        if (configOverride) {
          req.override = encodeURIComponent(JSON.stringify(configOverride))
        }
        return req
      }

      get(retryAtom)
      const venue = get(venueMatch)

      const datesValue = get(dates)
      const selectedDateValue = get(selectedDate)
      const category = get(selectedCategory)
      if (!position) {
        browserLogger.error('no place this should never happen')
        return undefined
      }
      const venueId = venue?.venueId || 'place'

      const req: RadiusRingsTradeAreaDetailsRequest = {
        center: position,
        dateRange: datesValue[selectedDateValue],
        naicsCode: category,
        venueId,
        radii: get(radii),
      }
      return req
    },
  lru: DEFAULT_LRU_SIZE,
})

export const tradeArea = uncachedSelectorFamily<RadiusRingsTradeAreaDetailsResponse | undefined, Position | undefined>({
  key: key('trade-area'),
  get:
    (position) =>
    ({ get }) => {
      const req = get(request(position))
      if (!req) {
        return undefined
      }
      if ('token' in req) {
        return ifNotFound(
          cacheGetRadiiTradeAreaDetails.get(req, (args) => partnersClient.getBuildoutRadiiTradeAreaDetails(args)),
          undefined
        )
      }
      return ifNotFound(
        cacheGetRadiiTradeAreaDetails.get(req, (args) => tradeAreaClient.getRadiiTradeAreaDetails(args)),
        undefined
      )
    },
  lru: DEFAULT_LRU_SIZE,
})

export const boundingBox = selectorFamily<BBox2d | undefined, Position | undefined>({
  key: key('bounding-box'),
  get:
    (position) =>
    ({ get }) => {
      const rads = get(radii)
      const lastRad = rads[rads.length - 1]
      if (!position) {
        return
      }
      const center = turf.point(position)
      const buffer = turf.buffer(center, lastRad, { units: 'miles' })
      return turf.bbox(buffer) as BBox2d
    },
})

export const hasDetails = selectorFamily<boolean, Position | undefined>({
  key: key('has-details'),
  get:
    (position) =>
    ({ get }) => {
      return get(tradeArea(position)) != null
    },
})

export const nearby = uncachedSelectorFamily<CommonTradeAreaDetailsResponse | undefined, Position | undefined>({
  key: key('common-details'),
  get:
    (position) =>
    ({ get }) => {
      const req = get(request(position))
      if (!req) {
        return undefined
      }
      return ifNotFound(
        cacheGetRadiusRingsNearby.get(req as RadiusRingsTradeAreaDetailsRequest, (args) =>
          tradeAreaClient.getRadiusRingsNearby(args)
        ),
        undefined
      )
    },
  lru: DEFAULT_LRU_SIZE,
})

const tradeAreaSections = uncachedSelectorFamily<CommonTradeAreaSection[], Position | undefined>({
  key: key('common-sections'),
  get:
    (position) =>
    async ({ get }) => {
      return get(tradeArea(position))?.sections ?? []
    },
  lru: DEFAULT_LRU_SIZE,
})

export const demographicsExpandedCategories = atom<string[]>({
  key: key('demographics-expanded-categories'),
  default: ['Overview', 'Households'],
})

export const demographicsData = uncachedSelectorFamily<
  DemoRadiiResponse | undefined,
  { position: Position | undefined; compare: boolean }
>({
  key: key('demographics-data'),
  get:
    ({ position, compare }) =>
    async ({ get }) => {
      const tradeAreas = get(tradeAreaSections(position))
      const rads = compare ? get(radiiCompare) : get(radii)
      return tradeAreas.map((area, i) => {
        const data = computeHispanicPercentage(area.demographics)
        return {
          data,
          consumerSpendData: area.consumerSpend,
          // TODO(bmc): don't depend on radius here in the future
          radius: compare ? rads[0] : rads[i],
        }
      })
    },
  lru: DEFAULT_LRU_SIZE,
})

export const venuesInTradeArea = uncachedSelectorFamily<NearVenue[], Position | undefined>({
  key: key('nearby-venues'),
  get:
    (position) =>
    ({ get }) => {
      const match = get(venueMatch)
      // this will never be the case here
      if (!match) {
        return []
      }
      const data = get(nearby(position))
      const venues = data?.nearbyVenues ?? []
      return venues.filter((x) => x.venueId !== match.venueId)
    },
  lru: DEFAULT_LRU_SIZE,
})

export const locationsInTradeArea = uncachedSelectorFamily<SimpleVenueWithDistance[], Position | undefined>({
  key: key('locations-in-trade-area'),
  get:
    (position) =>
    ({ get }) => {
      const data = get(nearby(position))
      return data?.nearbyLocations ?? []
    },
  lru: DEFAULT_LRU_SIZE,
})

export const nearbyLocationCounts = uncachedSelectorFamily<
  Array<number | undefined>,
  { position: Position | undefined }
>({
  key: key('nearby-simple-venues'),
  get:
    ({ position }) =>
    ({ get }) => {
      const data = get(tradeAreaSections(position))
      return data.map((x) => x.nearbyLocationCount)
    },
  lru: DEFAULT_LRU_SIZE,
})

type DemoOverview = {
  currentYear: number | undefined
  fiveYear: number | undefined
}

type View = {
  format: string
  name: string
  values: DemoOverview[]
}

type DemoOverviewRange = View[]

export const demoOverview = uncachedSelectorFamily<
  DemoOverviewRange | undefined,
  { position: Position | undefined; compare: boolean }
>({
  key: key('demo-overview'),
  get:
    ({ position, compare }) =>
    ({ get }) => {
      const demographics = get(demographicsData({ position, compare }))
      const consumerSpend = get(consumerSpendData({ position, compare }))
      if (!demographics || !consumerSpend) {
        return undefined
      }
      const result: DemoOverviewRange = []
      const totalPop = demographics.map((item) => {
        const { currentYear, fiveYear } = item.data.Overview.Population
        return { currentYear, fiveYear }
      })
      result.push({
        format: 'number',
        name: 'Total Population',
        values: totalPop,
      })

      const workdayPop = demographics.map((item) => {
        const { currentYear, fiveYear } = item.data.Overview['Workday Population']
        return { currentYear, fiveYear }
      })
      result.push({
        format: 'number',
        name: 'Workday Population',
        values: workdayPop,
      })

      const households = demographics.map((item) => {
        const { currentYear, fiveYear } = item.data.Households['Total Households']
        return { currentYear, fiveYear }
      })
      result.push({
        format: 'number',
        name: 'Total Households',
        values: households,
      })

      const income = demographics.map((item) => {
        const { currentYear, fiveYear } = item.data['Household Income']['Average Household Income']
        return { currentYear, fiveYear }
      })
      result.push({
        format: 'currency',
        name: 'Avg. Household Income',
        values: income,
      })

      const spend = consumerSpend.map((item) => {
        const currentYear = item.currentYear.total
        const fiveYear = item.fiveYear.total
        return { currentYear, fiveYear }
      })
      result.push({
        format: 'currency',
        name: 'Total Consumer Spend',
        values: spend,
      })

      return result
    },
  lru: DEFAULT_LRU_SIZE,
})

export const consumerSpendData = uncachedSelectorFamily<
  ConsumerSpendRadiusData[] | undefined,
  { position: Position | undefined; compare: boolean }
>({
  key: key('spend-data'),
  get:
    ({ position }) =>
    async ({ get }) => {
      const tradeAreas = get(tradeAreaSections(position))
      return tradeAreas.map((area) => {
        return area.consumerSpend
      })
    },
  lru: DEFAULT_LRU_SIZE,
})

export const sizeMiles = selector<Array<number | null>>({
  key: key('size-miles'),
  get: ({ get }) => {
    const radiiValues = get(radii)
    const areas = radiiValues.map((radius) => radius * radius * Math.PI)
    return areas
  },
})

export const demographicsCategories = selectorFamily<string[], { position: Position | undefined; compare: boolean }>({
  key: key('demographics-categories'),
  get:
    ({ position, compare }) =>
    ({ get }) => {
      const breakdown = get(demographicsData({ position, compare }))
      if (!breakdown) {
        return []
      }
      const keys = breakdown.flatMap((x) => Object.keys(x.data))
      return uniq(keys)
    },
})

const monthTrendsForVenue = uncachedSelectorFamily<TrendsResponse | undefined, string>({
  key: key('trend-for-venue'),
  get:
    (venueId) =>
    ({ get }) => {
      const months = get(trendsChartMonths)
      const start = months[0]
      const end = months[months.length - 1]
      if (!start || !end) {
        return undefined
      }
      return ifNotFound(
        footfallClient.getTrends({
          start,
          end,
          period: TrendsPeriod.MONTHLY,
          venueId,
        }),
        undefined
      )
    },
  lru: DEFAULT_LRU_SIZE,
})

export const trendsChartMonths = selector<string[]>({
  key: key('trends-chart-categories'),
  get: ({ get }) => {
    const dates = get(trendDatesByPeriod(TrendsPeriod.MONTHLY)).periods
    const months = dates.slice(-12)
    if (months.length < 3) {
      return []
    }
    return months
  },
})

export type TrendsChartSeriesData = {
  name: string
  data: number[]
  color?: string
}

export const trendsChartData = selectorFamily<TrendsChartSeriesData[], Position | undefined>({
  key: key('trends-chart-data'),
  get:
    (position) =>
    ({ get }) => {
      const venue = get(venueMatch)
      if (!venue) {
        return []
      }
      const venues = get(venuesInTradeArea(position))
      const nearby = venues.slice(0, 4)
      const ids = [venue.venueId].concat(nearby.map((x) => x.venueId))
      const venueTrends = ids.map((id) => get(monthTrendsForVenue(id)))
      const results: TrendsChartSeriesData[] = []
      for (let i = 0; i < venueTrends.length; i++) {
        const res = venueTrends[i]
        if (res == null) {
          continue
        }
        const name = i === 0 ? venue.brand ?? 'venue' : venues[i - 1].brand

        results.push({
          name,
          data: res.map((x) => x.personCountSum),
        })
      }
      return results
    },
})

export const trendsChartDataCompare = selector<TrendsChartSeriesData[]>({
  key: key('trends-chart-data-compare'),
  get: ({ get }) => {
    const colors = get(atomsColors.gradientsTopColors)
    const venue = get(venueMatch)
    if (!venue) {
      return []
    }
    const extraVenues = get(atomsCompare.venues)
    const extraVenuesIds: string[] = extraVenues.flatMap((x) => (x ? [x.venueId] : []))
    const ids = [venue.venueId, ...extraVenuesIds]
    const venueTrends = ids.map((id) => get(monthTrendsForVenue(id)))
    const results: TrendsChartSeriesData[] = []
    for (let i = 0; i < venueTrends.length; i++) {
      const res = venueTrends[i]
      if (res == null) {
        continue
      }
      const name = i === 0 ? venue.brand ?? 'venue' : extraVenues[i - 1]?.brand || '-'
      results.push({
        name,
        data: res.map((x) => x.personCountSum),
        color: colors[i],
      })
    }
    return results
  },
})

export const showTradeAreaSize = atom<boolean>({
  key: key('show-trade-area-size'),
  default: true,
})

export const showSameCategory = atom<boolean>({
  key: key('show-same-category'),
  default: true,
})

export const showDemographicOverview = atom<boolean>({
  key: key('show-demographic-Overview'),
  default: true,
})

export const showCoverageByDistance = atom<boolean>({
  key: key('show-coverage-by-distance'),
  default: true,
})

export const showTradeAreaOverlap = atom<boolean>({
  key: key('show-trade-area-overlap'),
  default: true,
})

export const demographicsDisplayCategories = atom<string[]>({
  key: key('demographics-display-categories'),
  default: ALL_DEMOGRAPHICS_CATEGORIES,
})

export const showConsumerSpend = atom<boolean>({
  key: key('show-consumer-spend'),
  default: true,
})

export const showWithinComparison = atom<boolean>({
  key: key('show-whithin-comparison'),
  default: true,
})

export const showNearestCategory = atom<boolean>({
  key: key('show-nearest-category'),
  default: true,
})

export const showTrendComparisonChart = atom<boolean>({
  key: key('show-trend-comparison-chart'),
  default: true,
})

export const isMapStatic = atom<boolean>({
  key: key('is-map-static'),
  default: false,
})
